import { useId } from 'react';

type BrandMarkMode = 'auto' | 'light' | 'dark';
type BrandMarkVariant = Exclude<BrandMarkMode, 'auto'>;

interface BrandMarkProps {
  className?: string;
  mode?: BrandMarkMode;
  title?: string;
  decorative?: boolean;
}

interface BrandLockupProps {
  className?: string;
  iconClassName?: string;
  textClassName?: string;
  mode?: BrandMarkMode;
}

function BrandMarkSvg({
  className = 'h-6 w-6',
  variant,
  title = 'Pollenate',
  decorative = true,
}: BrandMarkProps & { variant: BrandMarkVariant }) {
  const gradientId = useId();
  const petalGradientId = `${gradientId}-petal`;
  const hiveGradientId = `${gradientId}-hive`;
  const petalTop = variant === 'dark' ? '#F8FAFC' : '#0F172A';
  const petalBottom = variant === 'dark' ? '#94A3B8' : '#334155';

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 200"
      className={`${className} block shrink-0`}
      aria-hidden={decorative}
      role={decorative ? undefined : 'img'}
    >
      {!decorative ? <title>{title}</title> : null}
      <defs>
        <linearGradient id={hiveGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#EA580C" />
        </linearGradient>
        <linearGradient id={petalGradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={petalTop} />
          <stop offset="100%" stopColor={petalBottom} />
        </linearGradient>
      </defs>

      <g transform="translate(100 100)">
        <g id={`${gradientId}-petal-shape`}>
          <path d="M 0 -15 C 22 -28, 28 -63, 0 -83 C -28 -63, -22 -28, 0 -15 Z" fill={`url(#${petalGradientId})`} />
          <circle cx="0" cy="-65" r="4" fill="#FBBF24" />
        </g>

        <use href={`#${gradientId}-petal-shape`} transform="rotate(0)" />
        <use href={`#${gradientId}-petal-shape`} transform="rotate(60)" />
        <use href={`#${gradientId}-petal-shape`} transform="rotate(120)" />
        <use href={`#${gradientId}-petal-shape`} transform="rotate(180)" />
        <use href={`#${gradientId}-petal-shape`} transform="rotate(240)" />
        <use href={`#${gradientId}-petal-shape`} transform="rotate(300)" />

        <polygon
          points="0,-26 22.5,-13 22.5,13 0,26 -22.5,13 -22.5,-13"
          fill={`url(#${hiveGradientId})`}
          stroke="#FFFFFF"
          strokeWidth="4"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

export function BrandMark({ className = 'h-6 w-6', mode = 'auto', title, decorative = true }: BrandMarkProps) {
  if (mode === 'light' || mode === 'dark') {
    return <BrandMarkSvg className={className} variant={mode} title={title} decorative={decorative} />;
  }

  return (
    <span className="inline-flex items-center justify-center">
      <BrandMarkSvg className={`${className} dark:hidden`} variant="light" title={title} decorative={decorative} />
      <BrandMarkSvg className={`${className} hidden dark:block`} variant="dark" title={title} decorative={decorative} />
    </span>
  );
}

export function BrandLockup({
  className = '',
  iconClassName = 'h-6 w-6',
  textClassName = '',
  mode = 'auto',
}: BrandLockupProps) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`.trim()}>
      <BrandMark className={iconClassName} mode={mode} />
      <span className={textClassName}>Pollenate</span>
    </span>
  );
}